import ActionIcon from '@/components/ActionIcon';
import Link from '@/components/Link';
import { FiArrowRight, MdContentCopy, MdOutlineArrowOutward } from '@/components/react-icons/main/index.esm';
import Tag from '@/components/Tag';
import TextWithEllipsis from '@/components/TextWithEllipsis';
import IconV2 from '@/components/v2/IconV2';
import RowV2 from '@/components/v2/RowV2';
import copyToClipboard from '@/lib/copyToClipboard';
import selectors from '@/redux/selectors/selectors';
import activityModels from '@/screens/home/schema/activityModels';
import getTimeMachineEntry from '@/screens/workflows/redux/actions/getTimeMachineEntry';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { useSelector } from 'react-redux';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { getArgumentValue } from '@shared/workflow/getArgumentValue.js';
import setQuery from '@/lib/setQuery.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { query } from '@/redux/selectors/querySelectors.js';
import getUpdateTitle from '@/screens/workflows/workflowLogs/lib/getUpdateTitle';
import { useEffect, useState } from 'react';

const getUpdateDescription = (update) => {
  const message = update?.message;

  if (typeof message === 'object') {
    return message?.message;
  }

  if (message && typeof message === 'string') {
    return update?.message;
  }

  if (typeof activityModels?.[update?.model]?.(update?.presentation)?.description === 'string') {
    return activityModels?.[update?.model]?.(update?.presentation)?.description;
  }

  return '';
};

const renderField = (field) => (typeof field === 'string' ? field : '');

const getTime = (update) => {
  const date = new Date(update?.updatedOn);
  return date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
};

const useProcessedUpdateTitle = (updateTitle) => {
  const [processedUpdateTitle, setProcessedUpdateTitle] = useState(typeof updateTitle === 'object' ? '' : updateTitle);
  useEffect(() => {
    if (typeof updateTitle === 'object') {
      getArgumentValue({ arg: updateTitle }).then((value) => setProcessedUpdateTitle(value));
    }
  }, [updateTitle]);
  return processedUpdateTitle;
};

const LogWorkDoneRow = ({ update, hideStepNumber, hideId, noOnClick }) => {
  const activeLogId = useArgSelector(query, 'logId');
  const appsMetadata = useSelector(selectors.appsMetadata);
  const stepAppId = (state) => workflowScreenSelectors.activeWorkflowStep(state)?.steps?.find((s) => s.id === update?.stepId)?.appId;
  const appMetadata = appsMetadata?.find((a) => a.id === stepAppId) || null;
  const output = useArgSelector(selectors.workflowLogOutputById, activeLogId);

  const isValidActionStatus = (update) => ['warn', 'error', 'info'].includes(update?.actionStatus);
  const updateTitle = getUpdateTitle(update);
  const processedUpdateTitle = useProcessedUpdateTitle(updateTitle);

  const handleStepClick = () => {
    const { index } =
      getTimeMachineEntry({
        logId: activeLogId,
        operationLog: update
      }) || {};
    if (index) {
      setQuery({ time: index, logStepId: update?.stepId, logTab: 'timeline', scrollTo: update?.stepId });
    }
  };

  const onRowClick = (e) => {
    if (e.target.closest('[data-title]')) {
      return;
    }
    const { index } =
      getTimeMachineEntry({
        logId: activeLogId,
        operationLog: update
      }) || {};

    if (index !== undefined) {
      setModalState('logStepOutput', { show: true, title: getUpdateTitle(update) });
      setQuery({ time: index, logStepId: update?.stepId });
    }
  };

  return (
    <RowV2 className={'row'} onClick={noOnClick || !output ? undefined : onRowClick}>
      <RowV2.Left>
        <ActionIcon action={isValidActionStatus(update) ? update?.actionStatus : update?.operation} />
        <Link className={'link'} to={update?.presentation?.entityUrl} target="_blank" data-title={appMetadata?.name ? `View Record in ${appMetadata?.name}` : ''}>
          <TextWithEllipsis>{processedUpdateTitle}</TextWithEllipsis>
          {update?.presentation?.entityUrl && <IconV2 icon={MdOutlineArrowOutward} size={8} />}
        </Link>
        <TextWithEllipsis className={'message'}>{getUpdateDescription(update)}</TextWithEllipsis>
      </RowV2.Left>

      <RowV2.Right>
        {update?.updatedOn && (
          <Tag data-title={'Time'} variant="ghost" className="time-tag">
            {getTime(update)}
          </Tag>
        )}

        {!hideId && renderField(update?.presentation?.id) && (
          <Tag icon={MdContentCopy} data-title={'Copy ID'} size={'sm'} onClick={() => copyToClipboard(update?.presentation?.id, { toast: true })}>
            {update?.presentation?.id}
          </Tag>
        )}

        {!hideStepNumber && !!output && (
          <Tag icon={FiArrowRight} iconPosition={'right'} size={'sm'} data-title={`View Detailed Log - ${output?.meta?.steps?.find((n) => n?.id === update?.stepId)?.message}`} onClick={handleStepClick}>
            Step {output?.meta?.steps?.find((n) => n?.id === update?.stepId)?.stepNumber}
          </Tag>
        )}
      </RowV2.Right>
    </RowV2>
  );
};

export default LogWorkDoneRow;
